import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, X, Send, Sparkles, ExternalLink, RotateCcw, Zap, ChevronRight, FileDown } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useFiltersStore } from '@/store/filtersStore';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const pathToModule = (pathname) => {
  if (pathname.includes('ops')) return 'ops';
  if (pathname.includes('liderazgo')) return 'liderazgo';
  if (pathname.includes('near-misses')) return 'near-misses';
  if (pathname.includes('mapa')) return 'mapa';
  return 'dashboard';
};

// Typewriter hook
function useTypewriter(text, speed = 12, active = false) {
  const [displayed, setDisplayed] = useState('');
  const idxRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!active || !text) { setDisplayed(text || ''); return; }
    idxRef.current = 0;
    setDisplayed('');
    timerRef.current = setInterval(() => {
      idxRef.current += 1;
      setDisplayed(text.slice(0, idxRef.current));
      if (idxRef.current >= text.length) clearInterval(timerRef.current);
    }, speed);
    return () => clearInterval(timerRef.current);
  }, [text, active, speed]);

  return displayed;
}

function MessageBubble({ msg, isLast, onDownload, isDownloading }) {
  const isUser = msg.role === 'user';
  const animated = isLast && msg.role === 'assistant' && !msg.error;
  const text = useTypewriter(msg.content, 10, animated);
  const isTyping = animated && text.length < msg.content.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} gap-2`}
    >
      {!isUser && (
        <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5"
          style={{ background: 'linear-gradient(135deg, #00C853 0%, #4A9EFF 100%)' }}>
          <Bot size={11} className="text-white" />
        </div>
      )}
      <div className="flex flex-col gap-1.5 max-w-[86%]">
        <div
          className="rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed"
          style={{
            background: isUser
              ? 'linear-gradient(135deg, rgba(0,200,83,0.2) 0%, rgba(74,158,255,0.15) 100%)'
              : msg.error
                ? 'rgba(255,82,82,0.1)'
                : 'rgba(255,255,255,0.05)',
            border: isUser
              ? '1px solid rgba(0,200,83,0.35)'
              : msg.error
                ? '1px solid rgba(255,82,82,0.3)'
                : '1px solid rgba(255,255,255,0.07)',
            color: '#FFFFFF',
            backdropFilter: 'blur(8px)',
            boxShadow: isUser ? '0 2px 12px rgba(0,200,83,0.08)' : 'none',
          }}
        >
          <p className="whitespace-pre-wrap">{animated ? text : msg.content}</p>
          {animated && isTyping && (
            <span className="inline-block w-0.5 h-3 ml-0.5 align-middle rounded-full animate-pulse" style={{ background: '#00C853' }} />
          )}
          {msg.citations && msg.citations.length > 0 && (
            <div className="mt-2.5 pt-2 space-y-1" style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
              <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'rgba(255,255,255,0.45)' }}>Fuentes</p>
              {msg.citations.map((c, ci) => (
                <a key={ci} href={c} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 text-[9px] hover:underline truncate"
                  style={{ color: '#4A9EFF' }}
                >
                  <ExternalLink size={8} />
                  {c.replace(/https?:\/\//, '').slice(0, 55)}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Download DOCX button — only for assistant messages, only after typing done */}
        {!isUser && !msg.error && !isTyping && (
          <div className="flex justify-start pl-1">
            <button
              onClick={() => onDownload(msg.content)}
              disabled={isDownloading}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-semibold uppercase tracking-wider transition-all hover:opacity-80 active:scale-95"
              style={{
                background: 'rgba(0,200,83,0.1)',
                border: '1px solid rgba(0,200,83,0.25)',
                color: '#00C853',
              }}
              data-testid="ai-download-docx-btn"
              title="Descargar como documento Word"
            >
              <FileDown size={9} />
              {isDownloading ? 'Generando...' : 'Descargar DOCX'}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function ThinkingIndicator() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-2"
    >
      <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #00C853 0%, #4A9EFF 100%)' }}>
        <Bot size={11} className="text-white" />
      </div>
      <div className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-2xl"
        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.5)' }}>Analizando</span>
        <div className="flex gap-0.5 ml-1">
          {[0, 1, 2].map(i => (
            <motion.div key={i} className="w-1 h-1 rounded-full" style={{ background: '#00C853' }}
              animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1.2, 0.8] }}
              transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [downloadingIdx, setDownloadingIdx] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const { pathname } = useLocation();
  const module = pathToModule(pathname);
  const desde   = useFiltersStore(s => s.desde);
  const hasta   = useFiltersStore(s => s.hasta);
  const sede    = useFiltersStore(s => s.sede);
  const negocio = useFiltersStore(s => s.negocio);

  const token = localStorage.getItem('valoriza_token');

  const buildContext = () => ({
    module,
    filters: { desde: desde || null, hasta: hasta || null, sede: sede || null, negocio: negocio || null },
  });

  const { data: prompts = [] } = useQuery({
    queryKey: ['ai-prompts', module],
    queryFn: () => axios.get(`${API}/api/ai/prompts`, {
      params: { module },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(r => r.data),
    staleTime: 60_000,
    enabled: !!token,
  });

  const chatMutation = useMutation({
    mutationFn: (message) => axios.post(
      `${API}/api/ai/chat`,
      {
        message,
        history: messages.slice(-8).map(m => ({ role: m.role, content: m.content })),
        context_data: buildContext(),
      },
      { headers: { Authorization: `Bearer ${token}` } }
    ).then(r => r.data),
    onSuccess: (data, vars) => {
      setMessages(prev => [
        ...prev,
        { role: 'user', content: vars },
        { role: 'assistant', content: data.response, citations: data.citations },
      ]);
    },
    onError: () => {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Lo siento, ocurrió un error al consultar el asistente. Por favor, inténtalo de nuevo.',
        error: true,
      }]);
    },
  });

  const handleDownload = useCallback(async (text, idx) => {
    setDownloadingIdx(idx);
    try {
      const moduleLabels = {
        ops: 'OPS', liderazgo: 'Liderazgo', 'near-misses': 'Near Misses',
        mapa: 'Mapa', dashboard: 'Dashboard General',
      };
      const resp = await axios.post(
        `${API}/api/ai/export-doc`,
        {
          text,
          title: `Análisis ${moduleLabels[module] || 'General'} — ${new Date().toLocaleDateString('es-ES')}`,
          module: moduleLabels[module] || 'General',
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pando_ai_${new Date().toISOString().slice(0, 10)}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Documento descargado correctamente');
    } catch {
      toast.error('Error al generar el documento');
    } finally {
      setDownloadingIdx(null);
    }
  }, [module, token]);

  const send = useCallback((msg) => {
    const text = msg || input.trim();
    if (!text || chatMutation.isPending) return;
    setInput('');
    chatMutation.mutate(text);
  }, [input, chatMutation]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, chatMutation.isPending]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleClear = () => setMessages([]);

  return (
    <>
      {/* Floating button with glow pulse */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="fab"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            onClick={handleOpen}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.92 }}
            className="fixed bottom-6 right-6 w-13 h-13 rounded-full flex items-center justify-center z-40"
            style={{ width: 52, height: 52 }}
            data-testid="ai-assistant-button"
          >
            {/* Glow rings */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{ background: 'linear-gradient(135deg, #00C853, #4A9EFF)', opacity: 0.3 }}
              animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0, 0.3] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{ background: 'linear-gradient(135deg, #00C853, #4A9EFF)', opacity: 0.2 }}
              animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0, 0.2] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            />
            {/* Button surface */}
            <div className="relative w-full h-full rounded-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #00C853 0%, #1a8f50 50%, #0D47A1 100%)',
                boxShadow: '0 4px 24px rgba(0,200,83,0.5), 0 0 0 1px rgba(0,200,83,0.3)',
              }}>
              <Sparkles size={20} style={{ color: '#FFFFFF' }} />
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat"
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
            className="fixed bottom-6 right-6 z-50 flex flex-col"
            style={{
              width: 'min(460px, calc(100vw - 24px))',
              height: 'min(640px, calc(100vh - 90px))',
              background: 'rgba(6,21,46,0.92)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid rgba(0,200,83,0.18)',
              borderRadius: 22,
              boxShadow: '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,200,83,0.08), inset 0 1px 0 rgba(255,255,255,0.05)',
            }}
          >
            {/* Animated gradient header */}
            <div
              className="flex items-center justify-between px-4 py-3 flex-shrink-0 rounded-t-[22px] relative overflow-hidden"
              style={{ borderBottom: '1px solid rgba(0,200,83,0.1)' }}
            >
              {/* Subtle animated glow in header */}
              <motion.div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(90deg, rgba(0,200,83,0.06) 0%, rgba(74,158,255,0.04) 50%, rgba(0,200,83,0.06) 100%)',
                }}
                animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
              />
              <div className="relative flex items-center gap-2.5">
                <div className="relative w-8 h-8">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #00C853, #4A9EFF)' }}>
                    <Zap size={14} className="text-white" />
                  </div>
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{ border: '2px solid rgba(0,200,83,0.5)' }}
                    animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>
                <div>
                  <p className="font-montserrat font-bold text-sm text-white tracking-tight">Pando AI</p>
                  <div className="flex items-center gap-1">
                    <motion.div className="w-1.5 h-1.5 rounded-full" style={{ background: '#00C853' }}
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                    <p className="text-[9px] font-medium tracking-wide" style={{ color: '#00C853' }}>ACTIVO · Pando AI</p>
                  </div>
                </div>
              </div>
              <div className="relative flex items-center gap-1">
                {messages.length > 0 && (
                  <button onClick={handleClear} className="w-6 h-6 rounded-full flex items-center justify-center transition-colors hover:bg-white/10"
                    title="Limpiar conversación" data-testid="ai-clear-button">
                    <RotateCcw size={11} style={{ color: 'rgba(255,255,255,0.45)' }} />
                  </button>
                )}
                <button onClick={handleClose} className="w-6 h-6 rounded-full flex items-center justify-center transition-colors hover:bg-red-900/30"
                  data-testid="ai-close-button">
                  <X size={13} style={{ color: 'rgba(255,255,255,0.7)' }} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-3.5 py-3 space-y-3 scrollbar-thin">
              {messages.length === 0 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
                  {/* Welcome state */}
                  <div className="text-center py-5 px-2">
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 3, repeat: Infinity, repeatDelay: 4 }}
                    >
                      <Sparkles size={30} className="mx-auto mb-2.5" style={{ color: '#00C853' }} />
                    </motion.div>
                    <p className="font-montserrat text-sm font-bold text-white mb-1">Asistente de Seguridad</p>
                    <p className="text-[10px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      Consulta sobre datos de OPS, liderazgo, near misses,<br />normativas y recomendaciones de seguridad laboral.
                    </p>
                  </div>

                  {/* Suggested prompts */}
                  <div className="space-y-2">
                    <p className="text-[9px] uppercase font-bold tracking-widest px-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      Preguntas sugeridas
                    </p>
                    {prompts.slice(0, 4).map((p, i) => (
                      <motion.button
                        key={p.id}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + i * 0.08 }}
                        onClick={() => send(p.message)}
                        className="w-full text-left px-3 py-2.5 rounded-xl text-xs flex items-center gap-2 group transition-all"
                        style={{
                          background: 'rgba(0,200,83,0.04)',
                          border: '1px solid rgba(0,200,83,0.12)',
                          color: 'rgba(255,255,255,0.75)',
                        }}
                        data-testid={`ai-prompt-${p.id}`}
                      >
                        <ChevronRight size={10} style={{ color: '#00C853', flexShrink: 0 }} className="group-hover:translate-x-0.5 transition-transform" />
                        <span className="line-clamp-2">{p.label}</span>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}

              {messages.map((msg, i) => (
                <MessageBubble
                  key={i}
                  msg={msg}
                  isLast={i === messages.length - 1}
                  onDownload={(text) => handleDownload(text, i)}
                  isDownloading={downloadingIdx === i}
                />
              ))}

              {chatMutation.isPending && <ThinkingIndicator />}
            </div>

            {/* Compact prompts chips when chat active */}
            {messages.length > 0 && prompts.length > 0 && (
              <div className="px-3.5 pt-2 pb-0 flex-shrink-0">
                <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                  {prompts.slice(0, 5).map(p => (
                    <button
                      key={p.id}
                      onClick={() => send(p.message)}
                      className="flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-medium transition-colors hover:opacity-80"
                      style={{
                        background: 'rgba(74,158,255,0.1)',
                        border: '1px solid rgba(74,158,255,0.2)',
                        color: '#4A9EFF',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input area */}
            <div className="px-3.5 pt-2 pb-3 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="flex gap-2 items-end">
                <Textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Pregunta sobre seguridad, datos, normativas..."
                  disabled={chatMutation.isPending}
                  rows={2}
                  className="flex-1 text-xs resize-none"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: 'white',
                    borderRadius: 14,
                    minHeight: 56,
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  data-testid="ai-input"
                />
                <motion.button
                  onClick={() => send()}
                  disabled={!input.trim() || chatMutation.isPending}
                  whileHover={{ scale: input.trim() ? 1.05 : 1 }}
                  whileTap={{ scale: 0.93 }}
                  className="flex-shrink-0 self-end w-10 h-10 rounded-[14px] flex items-center justify-center transition-all"
                  style={{
                    background: input.trim()
                      ? 'linear-gradient(135deg, #00C853, #4A9EFF)'
                      : 'rgba(255,255,255,0.08)',
                    boxShadow: input.trim() ? '0 4px 16px rgba(0,200,83,0.4)' : 'none',
                    cursor: input.trim() ? 'pointer' : 'default',
                  }}
                  data-testid="ai-send-button"
                >
                  <Send size={14} style={{ color: input.trim() ? '#fff' : '#555' }} />
                </motion.button>
              </div>
              <p className="text-[9px] text-center mt-1.5" style={{ color: 'rgba(255,255,255,0.15)' }}>
                Enter para enviar · Shift+Enter para nueva línea
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
